import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Kanban, List, Clock, Settings, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { projectService } from '@/services/projectService'

const ProjectTabBar = ({ projectId }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: projectData, isLoading } = useQuery(
    ['projects', projectId],
    () => projectService.getProject(projectId)
  )

  const project = projectData?.data?.project

  const tabs = [
    { key: 'board', label: 'Board', icon: Kanban, path: `/projects/${projectId}/board` },
    { key: 'backlog', label: 'Backlog', icon: Layers, path: `/projects/${projectId}/backlog` },
    { key: 'list', label: 'List', icon: List, path: `/projects/${projectId}/list` },
    { key: 'time', label: 'Time Tracking', icon: Clock, path: `/projects/${projectId}/time` },
  ]

  const isSettingsActive = location.pathname === `/projects/${projectId}/settings`

  return (
    <div className="border-b border-border bg-background px-4 lg:px-6">
      <div className="flex items-center justify-between">
        {/* Left: project name + tabs */}
        <div className="flex items-center gap-4 min-w-0">
          {/* Project name */}
          {isLoading ? (
            <div className="flex items-center gap-2 shrink-0">
              <Skeleton className="w-6 h-6 rounded" />
              <Skeleton className="w-24 h-5" />
            </div>
          ) : project ? (
            <div className="flex items-center gap-2 shrink-0">
              <span
                className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: project.color }}
              >
                {project.name.charAt(0).toUpperCase()}
              </span>
              <span className="font-semibold text-sm truncate max-w-[150px]">{project.name}</span>
            </div>
          ) : null}

          {/* Tabs */}
          <nav className="flex items-center gap-0 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = location.pathname.startsWith(tab.path)
              return (
                <button
                  key={tab.key}
                  onClick={() => navigate(tab.path)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Right: settings icon */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'shrink-0 h-8 w-8',
                isSettingsActive && 'text-primary'
              )}
              onClick={() => navigate(`/projects/${projectId}/settings`)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Project settings</TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

export default ProjectTabBar
